// src/pages/content_pages/SearchExplanation.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../../styles/ContentPages.css';

const SearchExplanation = () => {
  return (
    <>
      <Helmet>
        <title>Aktiensuche: Intelligente Filterung und Analyse | DynaStoxx</title>
        <meta name="description" content="Nutze die DynaStoxx Aktiensuche mit über 20 Filtern für Bewertungskennzahlen, Dividenden und Wachstumsprognosen, um die passenden Aktien für deine Anlagestrategie zu finden." />
        <meta name="keywords" content="Aktiensuche, Aktienfilter, Aktienscreening, KGV-Filter, Dividendenaktien finden, Value-Aktien, Wachstumsaktien, Aktienanalyse" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://dynastoxx.com/search-explained" />
      </Helmet>
      
      <div className="value-investing-container">
      <header className="hero-section">
        <div className="container">
          <h1>Aktiensuche: Mit intelligenten Filtern die besten Aktien finden</h1>
          <p className="hero-subtitle">
            Entdecke wie du mit der DynaStoxx <Link to="/search">Suchfunktion</Link> genau die Aktien findest, die zu deiner individuellen Anlagestrategie passen
          </p>
        </div>
      </header>
      
      <main className="content-container">
        <section className="intro-section">
          <div className="container">
            <h2>Was macht die DynaStoxx Aktiensuche besonders?</h2>
            <p>
              Die richtige Aktie zu finden, ist wie die Suche nach der sprichwörtlichen Nadel im Heuhaufen – ohne die richtigen Werkzeuge 
              fast unmöglich. Die DynaStoxx Aktiensuche bietet dir über 20 individuelle Filter, die auf den wichstigen Kennzahlen des Valueinvestings und Prinzipien erfolgreicher 
              Investoren wie Warren Buffett, Peter Lynch und Benjamin Graham basieren. 
            </p>
            <p>
              Im Gegensatz zu einfachen Börsenportalen, die oft nur oberflächliche Informationen bieten, kannst du bei uns tief in die 
              Fundamentaldaten eintauchen. Ob Dividendenjäger, Value-Investor oder Wachstumsstratege – unsere <Link to="/search">Suchfunktion</Link> passt sich 
              deiner persönlichen Anlagestrategie an und nicht umgekehrt.
            </p>
          </div>
        </section>

        <section className="metrics-overview">
          <div className="container">
            <h2>Die wichtigsten Suchfilter im Überblick</h2>
            <p>
              Unsere Aktiensuche ist in drei zentrale Bereiche unterteilt: Bewertungskennzahlen, Dividenden & Profitabilität sowie 
              Wachstumsprognosen. Jeder dieser Bereiche gibt dir einen anderen Blickwinkel auf potenzielle Investments und hilft dir, 
              genau die Aktien zu finden, die deinen Kriterien entsprechen.
            </p>

            <div className="cards-grid">
              <div className="metric-card highlight">
                <h3>Bewertungskennzahlen</h3>
                <p>
                  Finde Aktien basierend auf klassischen Bewertungsmetriken wie KGV, KBV und Wert/EBITDA. 
                  Diese Kennzahlen sind besonders für Value-Investoren wichtig, die unterbewertete Unternehmen identifizieren möchten. 
                  Du kannst beispielsweise nach Unternehmen mit einem KGV unter 15 suchen, um potenziell günstig bewertete Aktien zu entdecken.
                </p>
              </div>

              <div className="metric-card highlight">
                <h3>Dividenden & Profitabilität</h3>
                <p>
                  Filtere nach Dividendenrendite, Gewinnmarge und operativer Marge, um finanziell gesunde Unternehmen mit attraktiven 
                  Ausschüttungen zu finden. Perfekt für Einkommensinvestoren, die regelmäßige Erträge aus ihrem Portfolio generieren 
                  möchten oder für Anleger, die besonderen Wert auf finanzielle Stabilität legen.
                </p>
              </div>

              <div className="metric-card highlight">
                <h3>Wachstumsprognosen</h3>
                <p>
                  Entdecke Unternehmen mit überdurchschnittlichem Wachstumspotenzial durch Filter für Umsatzwachstum, 
                  Gewinnwachstum und erwartete zukünftige Entwicklung. Diese Kennzahlen sind entscheidend für Wachstumsinvestoren, 
                  die in Unternehmen mit überdurchschnittlichen Expansionsaussichten investieren möchten.
                </p>
              </div>

              <div className="metric-card highlight">
                <h3>Echtzeit-Währungsumrechnung</h3>
                <p>
                  Alle Finanzkennzahlen werden automatisch in deine bevorzugte Währung umgerechnet. Wähle zwischen USD, EUR, GBP oder JPY 
                  und vergleiche internationale Aktien auf Augenhöhe. Dies ist besonders wertvoll für Anleger mit internationalem Portfolio, 
                  da es einen direkten Vergleich von Aktien aus verschiedenen Wirtschaftsräumen ermöglicht.
                </p>
              </div>

              <div className="metric-card highlight">
                <h3>Performance-Tracking</h3>
                <p>
                  Jede Aktie wird mit ihrer täglichen, wöchentlichen, monatlichen und jährlichen Performance angezeigt, 
                  sodass du Trends und Momentum leicht erkennen kannst. Diese Übersicht ermöglicht dir, nicht nur die fundamentale 
                  Bewertung zu betrachten, sondern auch das aktuelle Marktsentiment zu berücksichtigen.
                </p>
              </div>

              <div className="metric-card highlight">
                <h3>Vordefinierte Strategiefilter</h3>
                <p>
                  Nutze unsere fertigen Strategiefilter wie "Dividendenstars", "Value-Aktien" oder "Wachstumsaktien", um 
                  sofort Aktien zu finden, die bewährten Anlagestrategien entsprechen. Diese Schnellfilter sind ideal für 
                  Einsteiger oder wenn du schnell eine erste Auswahl treffen möchtest.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="value-principles">
          <div className="container">
            <h2>So findest du die besten Aktien für deine Strategie</h2>
            
            <div className="principles-grid">
              <div className="principle-card">
                <h3>1. Für Value-Investoren</h3>
                <p>
                  Konzentriere dich auf niedrige KGV-Werte (unter 15), ein niedriges KBV (unter 1,5) und eine solide Dividendenrendite (über 2%). 
                  Achte besonders auf Unternehmen mit hoher Eigenkapitalrendite und stabiler Gewinnmarge, da diese oft auf Wettbewerbsvorteile 
                  hindeuten, die Warren Buffett als "wirtschaftlichen Burggraben" bezeichnet.
                </p>
              </div>
              
              <div className="principle-card">
                <h3>2. Für Dividendenjäger</h3>
                <p>
                  Suche nach Aktien mit hoher Dividendenrendite (über 3,5%), aber achte gleichzeitig auf eine moderate Ausschüttungsquote 
                  (unter 70%), um die Nachhaltigkeit der Dividende zu gewährleisten. Hohe Gewinnmargen und stabile Umsätze sind 
                  zusätzliche Indikatoren für zuverlässige Dividendenzahlungen in der Zukunft.
                </p>
              </div>
              
              <div className="principle-card">
                <h3>3. Für Wachstumsinvestoren</h3>
                <p>
                  Filtere nach starkem Umsatz- und Gewinnwachstum (über 15%) sowie positiven Zukunftsprognosen für das nächste Jahr. 
                  Bei Wachstumsaktien sind höhere KGV-Werte akzeptabel, wenn das Unternehmen entsprechend schnell wächst. 
                  Achte auf die PEG-Ratio (KGV geteilt durch Wachstumsrate), die idealerweise unter 1 liegen sollte.
                </p>
              </div>
              
              <div className="principle-card">
                <h3>4. Für defensive Anleger</h3>
                <p>
                  Fokussiere dich auf etablierte Unternehmen mit hoher Marktkapitalisierung (über 10 Mrd.), niedriger Verschuldung 
                  und überdurchschnittlicher Dividendenrendite. Branchen wie Versorger, Konsumgüter und Gesundheit bieten oft 
                  stabile Erträge auch in wirtschaftlich schwierigen Zeiten.
                </p>
              </div>
              
              <div className="principle-card">
                <h3>5. Für Qualitätsinvestoren</h3>
                <p>
                  Suche nach Unternehmen mit einer hohen Eigenkapitalrendite, geringer Verschuldung und beständigem Gewinnwachstum. 
                  Diese "Qualitätsaktien" weisen oft eine überdurchschnittliche Performance über lange Zeiträume auf, 
                  besonders wenn sie zu vernünftigen Bewertungen gekauft werden.
                </p>
              </div>
              
              <div className="principle-card">
                <h3>6. Für Contrarian-Investoren</h3>
                <p>
                  Nutze unsere Performance-Filter, um nach Aktien zu suchen, die kurzfristig schlecht abgeschnitten haben (negative monatliche Performance), 
                  aber solide Fundamentaldaten aufweisen. Diese antizyklische Strategie kann besonders ertragreich sein, wenn der Markt 
                  bestimmte Sektoren oder Unternehmen überverkauft hat.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="common-mistakes">
          <div className="container">
            <h2>Häufige Fehler bei der Aktiensuche vermeiden</h2>
            
            <div className="mistakes-list">
              <div className="mistake-item">
                <h3>Nur auf eine Kennzahl fixieren</h3>
                <p>
                  Ein häufiger Fehler ist, Aktien nur nach einer einzigen Kennzahl auszuwählen, wie etwa einem niedrigen KGV oder einer hohen Dividendenrendite. 
                  Erfolgreiche Investoren betrachten immer mehrere Faktoren im Zusammenspiel. Eine hohe Dividendenrendite kann beispielsweise ein Warnsignal sein, 
                  wenn sie auf einen fallenden Aktienkurs zurückzuführen ist.
                </p>
              </div>
              
              <div className="mistake-item">
                <h3>Branchen nicht berücksichtigen</h3>
                <p>
                  Verschiedene Branchen haben typische Kennzahlenbereiche. Ein KGV von 15 mag für eine Bank hoch sein, ist aber für ein 
                  Technologieunternehmen möglicherweise günstig. Nutze unsere Sektorfilter, um Aktien innerhalb derselben Branche zu vergleichen 
                  und so aussagekräftigere Ergebnisse zu erhalten.
                </p>
              </div>
              
              <div className="mistake-item">
                <h3>Zu viele Filter gleichzeitig</h3>
                <p>
                  Wenn zu viele strenge Filter kombiniert werden, bleibt oft keine Aktie übrig. Beginne mit wenigen, 
                  für dich wichtigen Kriterien und erweitere die Suche schrittweise. Alternativ kannst du unsere vordefinierten 
                  Strategiefilter nutzen, die bereits ausbalancierte Kriterien enthalten.
                </p>
              </div>
              
              <div className="mistake-item">
                <h3>Vernachlässigung qualitativer Faktoren</h3>
                <p>
                  Unsere Suche bietet hervorragende quantitative Filter, aber vergiss nicht, auch qualitative Aspekte wie Geschäftsmodell, 
                  Wettbewerbsvorteile oder Managementqualität zu berücksichtigen. Die besten Investments verbinden starke Fundamentaldaten 
                  mit überzeugenden qualitativen Eigenschaften.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="faq-section">
          <div className="container">
            <h2>Häufig gestellte Fragen zur Aktiensuche</h2>
            
            <div className="faq-list">
              <div className="faq-item">
                <h3>Woher stammen die Daten für die Aktiensuche?</h3>
                <p>
                  Unsere Daten werden täglich aus verschiedenen zuverlässigen Finanzquellen aktualisiert. Wir kombinieren Marktdaten, 
                  Preisentwicklungen und Fundamentaldaten, um dir ein möglichst umfassendes und aktuelles Bild zu bieten. 
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Kann ich meine Suchergebnisse speichern?</h3>
                <p>
                  Wenn du ein registriertes Konto hast, kannst du interessante Aktien direkt deiner Watchlist hinzufügen und 
                  in dein Portfolio aufnehmen, um sie weiter zu beobachten. Dies erleichtert es dir, vielversprechende Kandidaten 
                  im Auge zu behalten und ihre Performance über Zeit zu verfolgen.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Wie oft werden die Aktiendaten aktualisiert?</h3>
                <p>
                  Kursdaten werden täglich aktualisiert, während fundamentale Daten und Analysten-Prognosen regelmäßig nach 
                  Quartals- und Jahresberichten der Unternehmen erneuert werden. Dividendenzahlungen und -ankündigungen fließen 
                  zeitnah in unsere Datenbank ein, sodass du immer auf dem neuesten Stand bist.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Kann ich auch nach ETFs oder Fonds suchen?</h3>
                <p>
                  Aktuell konzentriert sich unsere Suchfunktion auf Einzelaktien. Dies hat den Vorteil, dass wir spezifische 
                  fundamentale Kennzahlen anbieten können, die bei ETFs oder Fonds oft weniger aussagekräftig sind. Für die Zukunft 
                  planen wir jedoch, auch ETFs mit spezialisierten Filterkriterien in unsere Suche zu integrieren.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Welche Märkte und Länder werden abgedeckt?</h3>
                <p>
                  Unsere Datenbank umfasst Aktien aus den wichtigsten Märkten weltweit, darunter USA, Deutschland, Großbritannien, 
                  Schweiz, Frankreich, Niederlande, Schweden, Kanada, China und Japan. Du kannst gezielt nach Ländern filtern, 
                  um dein Investment auf bestimmte Regionen zu fokussieren.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="conclusion-cta">
          <div className="container">
            <h2>Starte jetzt deine Aktiensuche</h2>
            <p>
              Mit der DynaStoxx Aktiensuche erhältst du ein leistungsstarkes Werkzeug, um gezielt die Aktien zu finden, 
              die deiner persönlichen Anlagestrategie entsprechen. Von Bewertungskennzahlen über Dividendenrenditen bis hin zu 
              Wachstumsprognosen – du hast alle wichtigen Faktoren im Blick.
            </p>
            <div className="cta-buttons">
              <Link to="/search" className="primary-button">
                <i className="fas fa-search"></i>
                Aktiensuche jetzt starten
              </Link>
              <Link to="/register" className="secondary-button">
                <i className="fas fa-user-plus"></i>
                Konto erstellen für alle Funktionen
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="value-investing-footer">
        <div className="container">
          <p>
            Die hier dargestellten Informationen dienen ausschließlich Bildungszwecken und stellen keine Anlageberatung dar. 
            Investitionen an der Börse bergen Risiken, einschließlich des möglichen Verlusts des investierten Kapitals.
          </p>
        </div>
      </footer>
    </div>
    </>
  );
};

export default SearchExplanation;